import { useContext } from 'react';

import { getI18nData, isBrowser, useProductUrls } from '@lucid/utils';
import { graphql, useStaticQuery } from 'gatsby';
import { Drupal_Node } from 'generated/drupal-graphql';
import { LayoutContext } from '@lucid/components-react';

import { CustomDrupal } from 'types/types';

export const placeholderButtons: CustomDrupal.LinkInterface[] = [
  {
    title: 'Contact sales',
    link: {
      url: {
        path: '#placeholder',
      },
    },
    links: [],
    type: 'primary',
    primary: true,
    isButton: true,
    isPlaceholder: true,
    entityId: 'placeholder',
  },
];

export const placeholderLinks: CustomDrupal.LinkInterface[] = [
  {
    title: 'Log in',
    link: {
      url: {
        path: '#placeholder-link',
      },
    },
    links: [],
    type: 'primary',
    primary: true,
    isButton: false,
    isPlaceholder: true,
    entityId: 'placeholder-link',
  },
];

const useNavButtons = (
  username?: string,
  shouldHideSignUpButton = false,
): {
  topNavButtons: CustomDrupal.LinkInterface[];
  navButtons: CustomDrupal.LinkInterface[];
} => {
  const {
    language: { urlLangPrefix, i18nKey, languageKey },
  } = useContext(LayoutContext);
  const { drupal, i18nData } = useStaticQuery(
    graphql`
      {
        drupal {
          nodeQuery(filter: { conditions: { field: "nid", operator: IN, value: ["365", "572"] } }) {
            entities {
              entityId
              entityTranslations {
                entityLanguage {
                  id
                }
                entityUrl {
                  path
                }
              }
            }
          }
        }
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_nav_try_now
                mktg_nav_contact_sales
                mktg_nav_log_in
                mktg_nav_log_out
              }
            }
          }
        }
      }
    `,
  );

  const { productUrl } = useProductUrls(urlLangPrefix);

  if (!isBrowser()) {
    return {
      topNavButtons: [],
      navButtons: [],
    };
  }

  const i18n = getI18nData(i18nData, i18nKey);
  const entities = drupal?.nodeQuery?.entities as Drupal_Node[];
  const tryNowLinks = entities?.find(entity => entity.entityId === '572');
  const contactSalesLinks = entities?.find(entity => entity.entityId === '365');
  const tryNowTranslatedEntity = tryNowLinks?.entityTranslations?.find(
    ({ entityLanguage }) => entityLanguage?.id === languageKey,
  );
  const contactSalesTranslatedEntity = contactSalesLinks?.entityTranslations?.find(
    ({ entityLanguage }) => entityLanguage?.id === languageKey,
  );
  const tryNowTranslatedPath = tryNowTranslatedEntity?.entityUrl?.path;
  const contactSalesTranslatedPath = contactSalesTranslatedEntity?.entityUrl?.path;

  const loggedInButtons: CustomDrupal.LinkInterface[] = [
    {
      title: 'Try now',
      link: {
        url: {
          path: tryNowTranslatedPath || '/try-now',
        },
      },
      links: [],
    },
    {
      title: i18n('mktg-nav-contact-sales'),
      link: {
        url: {
          path: contactSalesTranslatedPath || '/contact/contact-sales',
        },
      },
      links: [],
      type: 'primary',
      primary: true,
      fieldBeaconElementDetail: 'contact-sales',
      entityId: 'contact-sales',
      isButton: true,
    },
  ];

  const topLevelInitialLoggedOutButtons: CustomDrupal.LinkInterface[] = [
    {
      title: i18n('mktg-nav-log-in'),
      link: {
        url: {
          path: `${productUrl}/${urlLangPrefix}users/login?activate=lucidchart`,
        },
      },
      links: [],
      fieldBeaconElementDetail: 'log-in',
      entityId: 'login',
    },
  ];

  const topLevelInitialLoggedInButtons: CustomDrupal.LinkInterface[] = [
    {
      title: i18n('mktg-nav-log-out'),
      link: {
        url: {
          path: `${productUrl}/${urlLangPrefix}users/logout`,
        },
      },
      links: [],
      fieldBeaconElementDetail: 'log-out',
      entityId: 'log-out',
    },
  ];

  const initialLoggedOutButtons: CustomDrupal.LinkInterface[] = [
    {
      title: i18n('mktg-nav-try-now'),
      link: {
        url: {
          path: tryNowTranslatedPath || '/try-now',
        },
      },
      links: [],
      fieldBeaconElementDetail: 'try-now',
      entityId: 'try-now',
    },
    {
      title: i18n('mktg-nav-contact-sales'),
      link: {
        url: {
          path: contactSalesTranslatedPath || '/contact/contact-sales',
        },
      },
      links: [],
      type: 'primary',
      primary: true,
      fieldBeaconElementDetail: 'contact-sales',
      entityId: 'contact-sales',
      isButton: true,
    },
  ];

  let loggedOutButtons = initialLoggedOutButtons;
  if (shouldHideSignUpButton) {
    loggedOutButtons = initialLoggedOutButtons.slice(0, 2);
  }

  return username
    ? {
        topNavButtons: topLevelInitialLoggedInButtons,
        navButtons: loggedInButtons,
      }
    : {
        topNavButtons: topLevelInitialLoggedOutButtons,
        navButtons: loggedOutButtons,
      };
};

export default useNavButtons;
